import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import Header from "../../../commons/Header/Header";
import Sidebar from "../../../commons/Sidebar/Sidebar";
import viewImage from "../../../assets/images/icons/view.svg";
import editImage from "../../../assets/images/icons/edit-2.svg";
import DeleteIcon from "../../../assets/images/icons/delete-w.svg";
import Star from "../../../assets/images/icons/star.svg";
import Profile from "../../../assets/images/profile.png";
import BlockIcon from "../../../assets/images/icons/block-icon.svg";
import FilterTrigger from "../../../commons/FilterTrigger/FilterTrigger";
import { FaSyncAlt } from "react-icons/fa";
import Download from "../../../assets/images/icons/download-icon.svg";
import PDFIcon from "../../../assets/images/icons/pdf-icon.svg";
import Girl from "../../../assets/images/girl.jpg";
import { Link, useNavigate } from "react-router-dom";
import TablePagination from "../../../commons/Pagination/TablePagination";
import { handleApiCall } from "../../../apis";
import { endpoints } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS, getOrderStatusLabel } from "../../../utils/constants";
import { Modal, Button } from "react-bootstrap";

import DateRangeFilterMenu from "../../../commons/DateRangeFilterMenu/DateRangeFilterMenu";
import { Col, Row } from "react-bootstrap";
import {
  LIST_RETURN_KEYS,
  persistListReturn,
  consumeListReturn,
} from "../../../utils/listNavigation";

const EMPTY_DATE_FILTER = { from: null, to: null };
const TABLE_PAGE_LIMIT = 10;

const getClientPageItems = (items, page, limit = TABLE_PAGE_LIMIT) => {
  const list = Array.isArray(items) ? items : [];
  const start = (Math.max(page, 1) - 1) * limit;
  return list.slice(start, start + limit);
};

const getClientTotalPages = (items, limit = TABLE_PAGE_LIMIT) =>
  Math.max(1, Math.ceil((Array.isArray(items) ? items.length : 0) / limit) || 1);

const toDateQueryParams = (filter) => {
  if (!filter?.from || !filter?.to) return {};
  return { start_date: filter.from, end_date: filter.to };
};

const isApiSuccess = (response) =>
  !response?.error &&
  (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS || response?.data?.code === 200);

const getSubscriptionList = (payload) => {
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const getSubscriptionPagination = (payload) => payload?.pagination || {};

const formatSubscriptionDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatBillingCycle = (item) => {
  const cycle = item?.subscription_plan_id?.billing_cycle || item?.billing_cycle;
  if (!cycle) return "N/A";

  if (cycle === "one_time") {
    const months = item?.subscription_plan_id?.plan_duration_months;
    return months ? `${months} Month${months > 1 ? "s" : ""}` : "One Time";
  }

  return String(cycle)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatSubscriptionAmount = (item) => {
  const amount = item?.amount ?? item?.payment ?? item?.price;
  if (amount === undefined || amount === null || amount === "") return "N/A";

  const currency = String(item?.currency || "gbp").toUpperCase();
  const symbol = currency === "GBP" ? "£" : currency === "USD" ? "$" : currency === "EUR" ? "€" : "";
  return `${symbol}${amount}`;
};

const getSubscriptionStatusLabel = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  if (status === 1 || normalized === "active") return "Active";
  if (status === 0 || normalized === "inactive") return "Inactive";
  if (normalized === "expired") return "Expired";
  if (normalized === "pending") return "Pending";
  return status ?? "N/A";
};

const getSubscriptionStatusClass = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  if (status === 1 || normalized === "active") return "badge-success";
  if (normalized === "expired" || status === 0 || normalized === "inactive") return "badge-danger";
  return "badge-warning";
};

const getCommissionList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
};

const getCommissionPagination = (payload, responseData) => {
  const pagination =
    (payload && !Array.isArray(payload) ? payload.pagination : null) ||
    responseData?.pagination ||
    {};

  return {
    totalPages: Number(pagination.totalPages || pagination.total_pages || 1) || 1,
  };
};

const formatCallDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** API `call_duration` is in minutes (e.g. 1.78). */
const formatCallDuration = (value) => {
  if (value === undefined || value === null || value === "") return "N/A";
  if (typeof value === "string" && /min|sec|hr|:/.test(value.toLowerCase())) {
    return value;
  }

  const minutesValue = Number(value);
  if (Number.isNaN(minutesValue) || minutesValue < 0) return String(value);

  const totalSeconds = Math.round(minutesValue * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")} Min ${String(seconds).padStart(2, "0")} Sec`;
};

const getCallCustomerName = (item) =>
  item?.customer?.full_name ||
  item?.customer_details?.full_name ||
  item?.customer_id?.full_name ||
  item?.customer_name ||
  "N/A";

const getCallCustomerMobile = (item) => {
  const phone =
    item?.customer?.phone ||
    item?.customer_details?.phone ||
    item?.customer_id?.phone ||
    item?.customer_mobile ||
    item?.phone;
  if (!phone) return "N/A";

  const countryCode =
    item?.customer?.country_code ||
    item?.customer_details?.country_code ||
    item?.customer_id?.country_code;

  return countryCode ? `+${countryCode} ${phone}` : phone;
};

const getCallDurationValue = (item) => item?.call_duration ?? item?.duration;

const getCallIdLabel = (item) => item?.order_number || item?._id || "N/A";

const getCallDateValue = (item) => item?.createdAt || item?.call_date;

const getCommissionTypeLabel = (value) => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "1" || normalized === "standard" || normalized === "standared") return "Standard";
  if (normalized === "2" || normalized === "discounted") return "Discounted";
  return value ?? "N/A";
};

const getCommissionTypeValue = (item) =>
  item?.commission ??
  item?.commission_type ??
  item?.commision_type ??
  item?.commission_name;

const isDiscountedCommissionLog = (item) => {
  const normalized = String(getCommissionTypeValue(item) ?? "").toLowerCase();
  return normalized === "2" || normalized === "discounted";
};

/** Active schedule (status 1) blocks adding another until it expires. */
const isPendingCommissionSchedule = (item) => Number(item?.status) === 1;

const getCommissionScheduleStatusLabel = (status) =>
  Number(status) === 1 ? "Active" : "Expired";

const getCommissionScheduleStatusClass = (status) =>
  Number(status) === 1 ? "badge-success" : "badge-danger";

const toScheduleInputDate = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) return String(value).slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const COMMISSION_TYPES = {
  STANDARD: "Standard",
  DISCOUNTED: "Discounted",
};

const isStandardCommissionType = (commissionType) =>
  String(commissionType).toLowerCase() === COMMISSION_TYPES.STANDARD.toLowerCase();

function ViewRestaurant() {
  const [show, setShow] = useState(false);
  const [RejectShow, setRejectShow] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approveForm, setApproveForm] = useState({
    startDate: "",
    endDate: "",
    commissionType: "",
  });
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurantLoading, setRestaurantLoading] = useState(false);
  const [restaurantData, setRestaurantData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsPage, setCallsPage] = useState(1);
  const [callsTotalPages, setCallsTotalPages] = useState(1);
  const [deliveryCities, setDeliveryCities] = useState([]);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [deliveryTotalPages, setDeliveryTotalPages] = useState(1);
  const [driversPage, setDriversPage] = useState(1);
  const [ordersDateFilter, setOrdersDateFilter] = useState(EMPTY_DATE_FILTER);
  const [callsDateFilter, setCallsDateFilter] = useState(EMPTY_DATE_FILTER);
  const [subscriptionDateFilter, setSubscriptionDateFilter] = useState(EMPTY_DATE_FILTER);
  const [driversDateFilter, setDriversDateFilter] = useState(EMPTY_DATE_FILTER);
  const [deliveryDateFilter, setDeliveryDateFilter] = useState(EMPTY_DATE_FILTER);
  const [commissionDateFilter, setCommissionDateFilter] = useState(EMPTY_DATE_FILTER);
  const [scheduleDateFilter, setScheduleDateFilter] = useState(EMPTY_DATE_FILTER);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [subscriptionTotalPages, setSubscriptionTotalPages] = useState(1);
  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionPage, setCommissionPage] = useState(1);
  const [commissionTotalPages, setCommissionTotalPages] = useState(1);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulePage, setSchedulePage] = useState(1);
  const [scheduleTotalPages, setScheduleTotalPages] = useState(1);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ start_date: "", end_date: "" });
  const [hasPendingSchedule, setHasPendingSchedule] = useState(false);
  const [showCommissionEndDateModal, setShowCommissionEndDateModal] = useState(false);
  const [commissionEndDateSubmitting, setCommissionEndDateSubmitting] = useState(false);
  const [commissionEndDateForm, setCommissionEndDateForm] = useState({
    end_date: "",
    start_date: "",
  });
  const [overallRating, setOverallRating] = useState({
    avgRating: 0,
    totalDish: 0,
    ratingBreakdown: {}
  });
  const [reviews, setReviews] = useState([]);

  const renderDateFilterMenu = (dropdownId, { appliedFrom, appliedTo, onApply, onReset }) => (
    <DateRangeFilterMenu
      isOpen={activeDropdown === dropdownId}
      appliedFrom={appliedFrom}
      appliedTo={appliedTo}
      onApply={(start, end) => {
        onApply(start, end);
        setActiveDropdown(null);
      }}
      onReset={() => {
        onReset?.();
        setActiveDropdown(null);
      }}
    />
  );

  const handleApproveShow = () => {
    setApproveForm({ startDate: "", endDate: "", commissionType: "" });
    setShow(true);
  };
  const handleRejectShow = () => {
    setRejectReason("");
    setRejectShow(true);
  };
  const handleClose = () => {
    setShow(false);
    setRejectShow(false);
    setApproveForm({ startDate: "", endDate: "", commissionType: "" });
    setRejectReason("");
  };

  const handleScheduleModalClose = () => {
    if (scheduleSubmitting) return;
    setShowScheduleModal(false);
    setEditingScheduleId(null);
    setScheduleForm({ start_date: "", end_date: "" });
  };

  const handleScheduleFormChange = (e) => {
    const { name, value } = e.target;
    setScheduleForm((prev) => ({ ...prev, [name]: value }));
  };

  const openAddScheduleModal = () => {
    if (hasPendingSchedule) {
      toast.error("An active schedule already exists. You can add a new one after it expires.");
      return;
    }
    setEditingScheduleId(null);
    setScheduleForm({ start_date: "", end_date: "" });
    setShowScheduleModal(true);
  };

  const openEditScheduleModal = (item) => {
    const scheduleId = item?._id;
    if (!scheduleId) {
      toast.error("Schedule ID not found");
      return;
    }
    setEditingScheduleId(scheduleId);
    setScheduleForm({
      start_date: toScheduleInputDate(item.start_date || item.startDate),
      end_date: toScheduleInputDate(item.end_date || item.endDate),
    });
    setShowScheduleModal(true);
  };

  const handleCommissionEndDateModalClose = () => {
    if (commissionEndDateSubmitting) return;
    setShowCommissionEndDateModal(false);
    setCommissionEndDateForm({ end_date: "", start_date: "" });
  };

  const openEditCommissionEndDateModal = (item) => {
    if (!isDiscountedCommissionLog(item)) {
      toast.error("Only discounted commission end date can be edited");
      return;
    }
    setCommissionEndDateForm({
      end_date: toScheduleInputDate(item.end_date || item.endDate),
      start_date: toScheduleInputDate(item.start_date || item.startDate),
    });
    setShowCommissionEndDateModal(true);
  };

  const handleApproveFormChange = (e) => {
    const { name, value } = e.target;
    setApproveForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "commissionType" && isStandardCommissionType(value)) {
        next.startDate = "";
        next.endDate = "";
      }
      return next;
    });
  };

  // const totalRatings = 886;
  // const averageRating = 4.8;

  // // Simulated rating data
  // const ratings = [
  //   { stars: 5, count: 7000 },
  //   { stars: 4, count: 200 },
  //   { stars: 3, count: 300 },
  //   { stars: 2, count: 20 },
  //   { stars: 1, count: 10 },
  // ];

  // Max count for progress bar reference
  // const maxCount = Math.max(...ratings.map((r) => r.count));
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleDropdown = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  const handleBackClick = () => {
    const { from, listState } = consumeListReturn(
      LIST_RETURN_KEYS.RESTAURANT.from,
      LIST_RETURN_KEYS.RESTAURANT.state,
      location.state
    );

    navigate(from || "/menu-and-restaurant-management", {
      state: listState
        ? {
            type: listState.tabType || "restaurant_list",
            listState,
          }
        : "restaurant_list",
    });
  };

  const handleApprove = async (e) => {
    e?.preventDefault();
    const restaurantId = restaurantData?.restaurant_id?._id;
    if (!restaurantId) {
      toast.error("Restaurant ID not found");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        approval_status: 1,
        commision_type: approveForm.commissionType,
      };

      if (!isStandardCommissionType(approveForm.commissionType)) {
        payload.start_date = approveForm.startDate;
        payload.end_date = approveForm.endDate;
      }

      const response = await handleApiCall(
        "patch",
        `${endpoints.restaurantApprovalStatus}/${restaurantId}/approval-status`,
        payload
      );

      if (response?.data?.code === 200) {
        toast.success(response?.data?.message || "Restaurant approved successfully");
        handleClose();
        getRestaurantDetails();
      }
    } catch (error) {
      console.error("Error approving restaurant:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestaurantStatusToggle = async () => {
    const restaurantUserId = restaurantData?.restaurant_id?._id;
    if (!restaurantUserId) {
      toast.error("Restaurant ID not found");
      return;
    }

    const currentStatus = Number(restaurantData?.status);
    const newStatus = currentStatus === 1 ? 0 : 1;
    setStatusUpdating(true);

    try {
      const response = await handleApiCall("patch", `${endpoints.restaurantStatus}/${restaurantUserId}`, {
        status: newStatus,
      });

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(
          response?.data?.message ||
            `Restaurant ${newStatus === 1 ? "activated" : "deactivated"} successfully`
        );
        getRestaurantDetails();
      }
    } catch (error) {
      console.error("Error updating restaurant status:", error);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a reject reason");
      return;
    }
    const restaurantId = restaurantData?.restaurant_id?._id;
    if (!restaurantId) {
      toast.error("Restaurant ID not found");
      return;
    }
    setActionLoading(true);
    try {
      const response = await handleApiCall("patch", `${endpoints.restaurantApprovalStatus}/${restaurantId}/approval-status`, {
        approval_status: 2,
        reject_reason: rejectReason.trim(),
      });

      if (response?.data?.code === 200) {
        toast.success(response?.data?.message || "Restaurant rejected successfully");
        handleClose();
        getRestaurantDetails();
      }
      // API error messages are already handled by handleApiCall
    } catch (error) {
      console.error("Error rejecting restaurant:", error);
      // Network errors or unexpected errors are already handled by handleApiCall
    } finally {
      setActionLoading(false);
    }
  };

  const getRestaurantDetails = useCallback(async () => {
    setRestaurantLoading(true);
    try {
      const response = await handleApiCall("get", `${endpoints.restaurantDetails}/${id}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setRestaurantData(response?.data?.data);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching restaurant details:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setRestaurantLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      sessionStorage.setItem("restaurantViewId", id);
      getRestaurantDetails();
    }
  }, [id, getRestaurantDetails]);

  useEffect(() => {
    if (location.state?.from || location.state?.listState) {
      persistListReturn(
        LIST_RETURN_KEYS.RESTAURANT.from,
        LIST_RETURN_KEYS.RESTAURANT.state,
        location.state?.from,
        location.state?.listState
      );
    }
  }, [location.state]);

  const restaurantId = restaurantData?.restaurant_id?._id;

  const fetchOverAllRating = useCallback(async () => {
    if (!restaurantId) return;

    try {
      const response = await handleApiCall(
        "get",
        `${endpoints.restaurantOverAllRatingOnDetail}?restaurant_id=${restaurantId}`
      );

      if (response?.data?.code === 200) {
        setOverallRating(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (restaurantId) {
      fetchOverAllRating();
    }
  }, [restaurantId, fetchOverAllRating]);
    
    const ratings = overallRating?.ratingBreakdown
  ? Object.keys(overallRating.ratingBreakdown)
      .sort((a, b) => b - a)
      .map((key) => ({
        stars: Number(key),
        count: overallRating.ratingBreakdown[key]?.count || 0,
        percentage: overallRating.ratingBreakdown[key]?.percentage || 0,
      }))
  : [];

const totalRatings = overallRating?.totalRestaurants || 0;

const fetchReviews = useCallback(async () => {
  if (!restaurantId) return;

  try {
    const response = await handleApiCall(
      "get",
      `${endpoints.restaurantReviewOnDetail}?restaurant_id=${restaurantId}`
    );

    if (response?.data?.code === 200) {
      setReviews(response?.data?.data?.review || []);
    }
  } catch (error) {
    console.error("Review API error", error);
  }
}, [restaurantId]);

useEffect(() => {
  if (restaurantId) {
    fetchReviews();
  }
}, [restaurantId, fetchReviews]);


const fetchDrivers = useCallback(async () => {
  if (!restaurantId) return;

  try {
    const params = {
      restaurant_id: restaurantId,
      ...toDateQueryParams(driversDateFilter),
    };

    const query = new URLSearchParams(params).toString();
    const url = `${endpoints.restaurantDriverList}?${query}`;

    const response = await handleApiCall("get", url);

    if (response?.error) {
      setDrivers([]);
      return;
    }

    if (isApiSuccess(response)) {
      const payload = response?.data?.data;
      const list = Array.isArray(payload?.result)
        ? payload.result
        : getCommissionList(payload);
      setDrivers(list);
      setDriversPage(1);
    } else {
      setDrivers([]);
      if (response?.data?.message) {
        toast.error(response.data.message);
      }
    }
  } catch (error) {
    console.error("Driver list error:", error);
    setDrivers([]);
  }
}, [restaurantId, driversDateFilter]);

useEffect(() => {
  if (restaurantId) {
    fetchDrivers();
  }
}, [restaurantId, fetchDrivers]);

const fetchSubscriptions = useCallback(async () => {
  if (!restaurantId) return;

  try {
    const params = {
      restaurant_id: restaurantId,
      page: subscriptionPage,
      limit: TABLE_PAGE_LIMIT,
      ...toDateQueryParams(subscriptionDateFilter),
    };

    const query = new URLSearchParams(params).toString();
    const response = await handleApiCall("get", `${endpoints.restaurantSubscriptionList}?${query}`);

    if (response?.error) {
      setSubscriptions([]);
      setSubscriptionTotalPages(1);
      return;
    }

    if (isApiSuccess(response)) {
      const responseData = response?.data?.data;
      const list = getSubscriptionList(responseData);
      const pagination = getSubscriptionPagination(responseData);
      setSubscriptions(list);
      setSubscriptionTotalPages(pagination.total_pages || pagination.totalPages || 1);
    } else {
      setSubscriptions([]);
      setSubscriptionTotalPages(1);
      if (response?.data?.message) {
        toast.error(response.data.message);
      }
    }
  } catch (error) {
    console.error("Subscription list error:", error);
    setSubscriptions([]);
    setSubscriptionTotalPages(1);
  }
}, [restaurantId, subscriptionPage, subscriptionDateFilter]);

useEffect(() => {
  if (restaurantId) {
    fetchSubscriptions();
  }
}, [restaurantId, fetchSubscriptions]);

const fetchCommissionSchedule = useCallback(async () => {
  if (!restaurantId) return;

  setSchedulesLoading(true);
  try {
    const params = {
      page: schedulePage,
      limit: TABLE_PAGE_LIMIT,
      ...toDateQueryParams(scheduleDateFilter),
    };

    const query = new URLSearchParams(params).toString();
    const response = await handleApiCall(
      "get",
      `${endpoints.restaurantCommissionScheduleList}/${restaurantId}/commission_schedule_list?${query}`
    );

    if (response?.error) {
      setSchedules([]);
      setScheduleTotalPages(1);
      return;
    }

    if (isApiSuccess(response)) {
      const payload = response?.data?.data;
      const list = getCommissionList(payload);
      const { totalPages } = getCommissionPagination(payload, response?.data);
      setSchedules(list);
      setScheduleTotalPages(totalPages);
    } else {
      setSchedules([]);
      setScheduleTotalPages(1);
      if (response?.data?.message) {
        toast.error(response.data.message);
      }
    }
  } catch (error) {
    console.error("Commission schedule list error:", error);
    setSchedules([]);
    setScheduleTotalPages(1);
  } finally {
    setSchedulesLoading(false);
  }
}, [restaurantId, schedulePage, scheduleDateFilter]);

const refreshPendingScheduleFlag = useCallback(async () => {
  if (!restaurantId) {
    setHasPendingSchedule(false);
    return;
  }

  try {
    const response = await handleApiCall(
      "get",
      `${endpoints.restaurantCommissionScheduleList}/${restaurantId}/commission_schedule_list?page=1&limit=${TABLE_PAGE_LIMIT}`
    );

    if (response?.error) {
      setHasPendingSchedule(false);
      return;
    }

    if (isApiSuccess(response)) {
      const list = getCommissionList(response?.data?.data);
      setHasPendingSchedule(list.some(isPendingCommissionSchedule));
    } else {
      setHasPendingSchedule(false);
    }
  } catch (error) {
    console.error("Pending commission schedule check error:", error);
  }
}, [restaurantId]);

useEffect(() => {
  if (restaurantId) {
    fetchCommissionSchedule();
  }
}, [restaurantId, fetchCommissionSchedule]);

useEffect(() => {
  if (restaurantId) {
    refreshPendingScheduleFlag();
  }
}, [restaurantId, refreshPendingScheduleFlag]);

const handleSaveCommissionSchedule = async (e) => {
  e?.preventDefault();
  if (!restaurantId) {
    toast.error("Restaurant ID not found");
    return;
  }
  if (!editingScheduleId && hasPendingSchedule) {
    toast.error("An active schedule already exists. You can add a new one after it expires.");
    return;
  }
  if (!scheduleForm.start_date || !scheduleForm.end_date) {
    toast.error("Please select start date and end date");
    return;
  }
  if (scheduleForm.end_date < scheduleForm.start_date) {
    toast.error("End date cannot be before start date");
    return;
  }

  setScheduleSubmitting(true);
  try {
    const payload = {
      start_date: scheduleForm.start_date,
      end_date: scheduleForm.end_date,
    };

    const response = editingScheduleId
      ? await handleApiCall(
          "patch",
          `${endpoints.updateCommissionSchedule}/${restaurantId}/update_commission_schedule/${editingScheduleId}`,
          payload
        )
      : await handleApiCall(
          "post",
          `${endpoints.scheduleDiscountedCommission}/${restaurantId}/schedule_discounted_commission`,
          payload
        );

    if (isApiSuccess(response) || response?.data?.code === 201) {
      toast.success(
        response?.data?.message ||
          (editingScheduleId
            ? "Commission schedule updated successfully"
            : "Commission schedule added successfully")
      );
      setShowScheduleModal(false);
      setEditingScheduleId(null);
      setScheduleForm({ start_date: "", end_date: "" });
      if (!editingScheduleId) {
        setHasPendingSchedule(true);
        setSchedulePage(1);
      }
      fetchCommissionSchedule();
      refreshPendingScheduleFlag();
    }
  } catch (error) {
    console.error("Save commission schedule error:", error);
  } finally {
    setScheduleSubmitting(false);
  }
};

const handleDeleteCommissionSchedule = async (scheduleId) => {
  if (!restaurantId) {
    toast.error("Restaurant ID not found");
    return;
  }
  if (!scheduleId) {
    toast.error("Schedule ID not found");
    return;
  }
  if (!window.confirm("Are you sure you want to delete this commission schedule?")) {
    return;
  }

  setDeletingScheduleId(scheduleId);
  try {
    const response = await handleApiCall(
      "delete",
      `${endpoints.deleteCommissionSchedule}/${restaurantId}/commission_schedule/${scheduleId}`
    );

    if (isApiSuccess(response)) {
      toast.success(response?.data?.message || "Commission schedule deleted successfully");
      fetchCommissionSchedule();
      refreshPendingScheduleFlag();
    }
  } catch (error) {
    console.error("Delete commission schedule error:", error);
  } finally {
    setDeletingScheduleId(null);
  }
};

const handleUpdateCommissionEndDate = async (e) => {
  e?.preventDefault();
  if (!restaurantId) {
    toast.error("Restaurant ID not found");
    return;
  }
  if (!commissionEndDateForm.end_date) {
    toast.error("Please select end date");
    return;
  }
  if (
    commissionEndDateForm.start_date &&
    commissionEndDateForm.end_date < commissionEndDateForm.start_date
  ) {
    toast.error("End date cannot be before start date");
    return;
  }

  setCommissionEndDateSubmitting(true);
  try {
    const response = await handleApiCall(
      "patch",
      `${endpoints.updateCommissionEndDate}/${restaurantId}/update_commission_end_date`,
      {
        end_date: commissionEndDateForm.end_date,
      }
    );

    if (isApiSuccess(response) || response?.data?.code === 201) {
      toast.success(response?.data?.message || "Commission end date updated successfully");
      setShowCommissionEndDateModal(false);
      setCommissionEndDateForm({ end_date: "", start_date: "" });
      fetchCommissionLog();
    }
  } catch (error) {
    console.error("Update commission end date error:", error);
  } finally {
    setCommissionEndDateSubmitting(false);
  }
};

const fetchCommissionLog = useCallback(async () => {
  if (!restaurantId) return;

  setCommissionsLoading(true);
  try {
    const params = {
      page: commissionPage,
      limit: TABLE_PAGE_LIMIT,
      ...toDateQueryParams(commissionDateFilter),
    };

    const query = new URLSearchParams(params).toString();
    const response = await handleApiCall(
      "get",
      `${endpoints.restaurantCommissionLog}/${restaurantId}/commission_log?${query}`
    );

    if (response?.error) {
      setCommissions([]);
      setCommissionTotalPages(1);
      return;
    }

    if (isApiSuccess(response)) {
      const payload = response?.data?.data;
      const list = getCommissionList(payload);
      const { totalPages } = getCommissionPagination(payload, response?.data);
      setCommissions(list);
      setCommissionTotalPages(totalPages);
    } else {
      setCommissions([]);
      setCommissionTotalPages(1);
      if (response?.data?.message) {
        toast.error(response.data.message);
      }
    }
  } catch (error) {
    console.error("Commission log error:", error);
    setCommissions([]);
    setCommissionTotalPages(1);
  } finally {
    setCommissionsLoading(false);
  }
}, [restaurantId, commissionPage, commissionDateFilter]);

useEffect(() => {
  if (restaurantId) {
    fetchCommissionLog();
  }
}, [restaurantId, fetchCommissionLog]);

const fetchOrders = useCallback(async () => {
  if (!restaurantId) return;

  try {
    const params = {
      page: ordersPage,
      limit: TABLE_PAGE_LIMIT,
      ...toDateQueryParams(ordersDateFilter),
    };

    const query = new URLSearchParams(params).toString();
    const endpoint = `${endpoints.restaurantOrderList}/${restaurantId}?${query}`;

    const response = await handleApiCall("get", endpoint);

    if (response?.data?.code === 200) {
      const payload = response.data?.data;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.result)
          ? payload.result
          : Array.isArray(payload)
            ? payload
            : [];
      setOrders(list);
      setOrdersTotalPages(getCommissionPagination(payload, response?.data).totalPages);
    } else {
      setOrders([]);
      setOrdersTotalPages(1);
    }
  } catch (error) {
    console.error("Order API error", error);
    setOrders([]);
    setOrdersTotalPages(1);
  }
}, [restaurantId, ordersPage, ordersDateFilter]);

useEffect(() => {
  if (restaurantId) {
    fetchOrders();
  }
}, [restaurantId, fetchOrders]);

const fetchCalls = useCallback(async () => {
  if (!restaurantId) return;

  setCallsLoading(true);
  try {
    const params = {
      page: callsPage,
      limit: TABLE_PAGE_LIMIT,
      ...toDateQueryParams(callsDateFilter),
    };

    const query = new URLSearchParams(params).toString();
    const response = await handleApiCall(
      "get",
      `${endpoints.aiPhoneOrders}/${restaurantId}?${query}`
    );

    if (response?.error) {
      setCalls([]);
      setCallsTotalPages(1);
      return;
    }

    if (isApiSuccess(response)) {
      const payload = response?.data?.data;
      const list = Array.isArray(payload?.orders)
        ? payload.orders
        : getCommissionList(payload);
      const total =
        Number(payload?.pagination?.totalPages || payload?.pagination?.total_pages) ||
        getCommissionPagination(payload, response?.data).totalPages;
      setCalls(list);
      setCallsTotalPages(total || 1);
    } else {
      setCalls([]);
      setCallsTotalPages(1);
    }
  } catch (error) {
    console.error("AI phone orders API error", error);
    setCalls([]);
    setCallsTotalPages(1);
  } finally {
    setCallsLoading(false);
  }
}, [restaurantId, callsPage, callsDateFilter]);

useEffect(() => {
  if (restaurantId) {
    fetchCalls();
  }
}, [restaurantId, fetchCalls]);

const fetchDeliveryPincodes = useCallback(async () => {
  if (!restaurantId) return;

  try {
    const params = {
      restaurant_id: restaurantId,
      page: deliveryPage,
      limit: TABLE_PAGE_LIMIT,
      ...toDateQueryParams(deliveryDateFilter),
    };
    const query = new URLSearchParams(params).toString();
    const response = await handleApiCall(
      "get",
      `${endpoints.restaurantDelivaryChargList}?${query}`
    );

    if (response?.data?.code === 200) {
      const payload = response.data.data;
      setDeliveryCities(payload?.result || []);
      setDeliveryTotalPages(getCommissionPagination(payload, response?.data).totalPages);
    } else {
      setDeliveryCities([]);
      setDeliveryTotalPages(1);
    }
  } catch (error) {
    console.error("Error fetching delivery pincodes:", error);
    setDeliveryCities([]);
    setDeliveryTotalPages(1);
  }
}, [restaurantId, deliveryPage, deliveryDateFilter]);

useEffect(() => {
  if (restaurantId) {
    fetchDeliveryPincodes();
  }
}, [restaurantId, fetchDeliveryPincodes]);

const renderStars = (rating) => {
  const totalStars = 5;
  const filledStars = Number(rating);

  return (
    <>
      {[...Array(totalStars)].map((_, index) => (
        <span key={index}>
          {index < filledStars ? "★" : "☆"}
        </span>
      ))}
    </>
  );
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });
};

  const driversTotalPages = getClientTotalPages(drivers);
  const paginatedDrivers = getClientPageItems(drivers, driversPage);

  if (restaurantLoading) {
    return (
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="heading mb-3 d-flex align-items-center">
                <h3 className="text-default weight-400 my-0">View Restaurant</h3>
              </div>
              <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="heading mb-3 d-flex align-items-center">
                <h3 className="text-default weight-400 my-0">View Restaurant</h3>
              </div>
              <div>
                <Row cl>
                  <Col md="3" className="mt-3">
                    <button className="btn btn-default rounded-lg" onClick={handleBackClick}>
                      Back
                    </button>
                  </Col>
                  <Col md="6" className="text-center text-white">
                    <span className="font-weight-600">Status</span> :{" "}
                    <span className={restaurantData?.approval_status === 1 ? "text-success" : restaurantData?.approval_status === 2 ? "text-danger" : "text-warning"}>
                      {restaurantData?.approval_status === 1 ? "Approved" : restaurantData?.approval_status === 2 ? "Rejected" : "Pending"}
                    </span>
                    <span className="mx-3">|</span>
                    <span className="font-weight-600">Timing</span> : ({restaurantData?.opening_time || "N/A"} - {restaurantData?.closing_time || "N/A"})
                  </Col>
                  <Col md="3" className="text-end">
                    <div className="d-flex justify-content-end flex-wrap">
                      {restaurantData?.restaurant_id?._id && (
                        <Link
                          to={`/menu-and-restaurant/view-restaurant/${id}/wallet-ledger?restaurant_id=${restaurantData.restaurant_id._id}`}
                          state={{
                            restaurantName: restaurantData.restaurant_id?.restaurant_name || "Restaurant",
                            from: `/menu-and-restaurant/view-restaurant/${id}`,
                          }}
                          className="btn btn-primary rounded-lg px-4 mr-2 mb-2"
                        >
                          View Ledger
                        </Link>
                      )}
                      {restaurantData?.approval_status !== 0 && restaurantData?.restaurant_id?._id && (
                        <Link
                          to={`/menu-and-restaurant/view-menu-catalogue/${restaurantData.restaurant_id._id}/${encodeURIComponent(
                            restaurantData.restaurant_id?.restaurant_name || "Restaurant"
                          )}`}
                          className="btn btn-primary rounded-lg px-4 mb-2"
                        >
                          View Menu Catalogue
                        </Link>
                      )}
                    </div>
                  </Col>
                </Row>

                <div className="profile-card pt-3 mt-n2">
                  <img src={restaurantData?.logo || Profile} alt="Restaurant Logo" className="profile-img mb-3" />
                  <h4 className="fw-bold mb-0">{restaurantData?.restaurant_id?.restaurant_name || "N/A"}</h4>
                  {/* Only show Approve/Reject buttons for pending restaurants (approval_status === 0) */}
                  {restaurantData?.approval_status === 0 && (
                    <div className="mt-2">
                      <button className="btn btn-success rounded-lg px-3 mr-2" onClick={handleApproveShow}>
                        {actionLoading ? "Processing..." : "Approve"}
                      </button>
                      <button className="btn btn-danger rounded-lg px-3" onClick={handleRejectShow}>
                        {actionLoading ? "Processing..." : "Reject"}
                      </button>
                    </div>
                  )}
                  {/* Show Active/Inactive button for approved/rejected restaurants */}
                  {restaurantData?.approval_status !== 0 && (
                    <button
                      type="button"
                      className={`btn status-badge-btn ${Number(restaurantData?.status) === 1 ? "btn-success" : "btn-secondary"} mt-2 rounded-lg px-3`}
                      onClick={handleRestaurantStatusToggle}
                      disabled={statusUpdating}
                    >
                      <img src={BlockIcon} className="mr-2" alt="Status Icon" />
                      {statusUpdating
                        ? "Updating..."
                        : Number(restaurantData?.status) === 1
                          ? "Active"
                          : "Inactive"}
                    </button>
                  )}
                </div>
              </div>
              <div className="border rounded-14 py-2 px-3 mt-5">
                <div className="row mx-n2">
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Restaurant Id</strong>
                      <p className="mb-0 mt-2">#{restaurantData?._id?.slice(-8) || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Mobile Number</strong>
                      <p className="mb-0 mt-2">{restaurantData?.restaurant_id?.phone ? `+${restaurantData.restaurant_id.phone}` : "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Email id</strong>
                      <p className="mb-0 mt-2">{restaurantData?.restaurant_id?.email || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Address</strong>
                      <p className="mb-0 mt-2">
                        {(() => {
                          const address = restaurantData?.address;
                          if (address) {
                            return `${address.full_address || ""} ${address.pincode ? `- ${address.pincode}` : ""}`.trim();
                          }
                          return "N/A";
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Cuisine Types</strong>
                      <p className="mb-0 mt-2">
                        {restaurantData?.restaurant_cuisine && restaurantData.restaurant_cuisine.length > 0
                          ? restaurantData.restaurant_cuisine
                              .map((item) => item?.cuisine_id?.name)
                              .filter(Boolean)
                              .join(", ")
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>FSA Hygeine Number</strong>
                      <p className="mb-0 mt-2">{restaurantData?.fsa_hygine_number || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Delivery Coverage</strong>
                      <p className="mb-0 mt-2">{restaurantData?.delivery_range_id?.name || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Pin Code</strong>
                      <p className="mb-0 mt-2">{restaurantData?.address?.pincode || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Stripe ID</strong>
                      <p className="mb-0 mt-2">
                        {restaurantData?.restaurant_id?.stripe_account_id ||
                          restaurantData?.stripe_account_id ||
                          "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>CRN Number</strong>
                      <p className="mb-0 mt-2">{restaurantData?.crn_number || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border d-flex align-items-center p-3">
                      <div className="flex-fill">
                        <strong>FSA Hygiene Number</strong>
                        <div className="small d-flex align-items-center mt-2">
                          <img src={PDFIcon} alt="pdf" className="mr-2" />
                          {restaurantData?.fsa_hygine_certificate ? (
                            <a href={restaurantData.fsa_hygine_certificate} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-primary">
                              FSA Hygiene Document
                            </a>
                          ) : (
                            "No document available"
                          )}
                        </div>
                      </div>
                      {restaurantData?.fsa_hygine_certificate && (
                        <a href={restaurantData.fsa_hygine_certificate} target="_blank" rel="noopener noreferrer">
                          <img src={Download} alt="download" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border d-flex align-items-center p-3">
                      <div className="flex-fill">
                        <strong>ID Proof</strong>
                        <div className="small d-flex align-items-center mt-2">
                          <img src={PDFIcon} alt="pdf" className="mr-2" />
                          {restaurantData?.id_proof ? (
                            <a href={restaurantData.id_proof} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-primary">
                              ID Proof Document
                            </a>
                          ) : (
                            "No document available"
                          )}
                        </div>
                      </div>
                      {restaurantData?.id_proof && (
                        <a href={restaurantData.id_proof} target="_blank" rel="noopener noreferrer">
                          <img src={Download} alt="download" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border d-flex align-items-center p-3">
                      <div className="flex-fill">
                        <strong>Proof Of Ownership</strong>
                        <div className="small d-flex align-items-center mt-2">
                          <img src={PDFIcon} alt="pdf" className="mr-2" />
                          {restaurantData?.owner_ship ? (
                            <a href={restaurantData.owner_ship} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-primary">
                              Ownership Document
                            </a>
                          ) : (
                            "No document available"
                          )}
                        </div>
                      </div>
                      {restaurantData?.owner_ship && (
                        <a href={restaurantData.owner_ship} target="_blank" rel="noopener noreferrer">
                          <img src={Download} alt="download" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {restaurantData?.approval_status !== 0 && (
              <>
              <h4 className="mt-4 mb-0 pt-2">REVIEW AND RATING</h4>
              <div className="d-inline-flex border col-md-6 px-3 py-2 mt-3 rounded-14">
                <div className="row align-items-center flex-fill">
                  <div className="col-md-3">
                    <div className="display-4 d-flex align-items-center">
                      {/* {averageRating}{" "} */}
                      {Number(overallRating?.avgRating || 0).toFixed(1)}
                      <span className="text-warning">
                        <img src={Star} className="ml-1" style={{ top: "-4px", position: "relative" }} alt="Star" />
                      </span>
                    </div>
                    <div className="text-muted">{totalRatings} Rating</div>
                  </div>
                  <div className="col-md-9 border-left">
                    {ratings.map((rating) => {
                      // const barWidth = (rating.count / maxCount) * 100;
                      const barWidth = rating.percentage;
                      return (
                        <div key={rating.stars} className="d-flex align-items-center my-2">
                          <div style={{ width: "30px" }} className="d-flex align-items-center">
                            {rating.stars} <img src={Star} className="ml-2" style={{ height: "10px", top: "-1px", position: "relative" }} alt="Star" />
                          </div>
                          <div className="progress flex-grow-1 mx-2" style={{ height: "6px", borderRadius: "20px" }}>
                            <div
                              className="progress-bar bg-white"
                              role="progressbar"
                              style={{ width: `${barWidth}%` }}
                              aria-valuenow={rating.count}
                              aria-valuemin="0"
                              // aria-valuemax={maxCount}
                              aria-valuemax="100"
                            ></div>
                          </div>
                          <div style={{ width: "50px" }}>{rating.count}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* <div className="review_wrapper">
                <div className="p-3 border-1 rounded-14 mt-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center">
                      <img src={Girl} alt="User avatar" className="rounded-circle me-2" width="40" height="40" />
                      <div className="d-flex flex-column">
                        <div className="fw-bold">Carlos Ward</div>
                        <div className="text-warning small">★ ★ ★ ☆ ☆</div>
                      </div>
                    </div>
                    <div className="text-muted small fw-semibold">20 May</div>
                  </div>
                  <div className="mt-2 text-muted" style={{ fontSize: "14px", lineHeight: "1.5" }}>
                    Lorem Ipsum, Or Lipsum As It Is Sometimes Know Lorem Ipsum, Or Lipsum As It Is Sometime This Lorem Ipsum, Or Lipsum As It Is Sometime On Lorem Ipsum, Or Lipsum
                    As It Is Sometimes Know Lorem Ipsum, Or Lipsum As It Is Sometime This Lorem Ipsum, Or Lipsum As It Is Sometime On
                  </div>
                </div>
                <div className="p-3 border-1 rounded-14 mt-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center">
                      <img src={Girl} alt="User avatar" className="rounded-circle me-2" width="40" height="40" />
                      <div className="d-flex flex-column">
                        <div className="fw-bold">Carlos Ward</div>
                        <div className="text-warning small">★ ★ ★ ☆ ☆</div>
                      </div>
                    </div>
                    <div className="text-muted small fw-semibold">20 May</div>
                  </div>
                  <div className="mt-2 text-muted" style={{ fontSize: "14px", lineHeight: "1.5" }}>
                    Lorem Ipsum, Or Lipsum As It Is Sometimes Know Lorem Ipsum, Or Lipsum As It Is Sometime This Lorem Ipsum, Or Lipsum As It Is Sometime On Lorem Ipsum, Or Lipsum
                    As It Is Sometimes Know Lorem Ipsum, Or Lipsum As It Is Sometime This Lorem Ipsum, Or Lipsum As It Is Sometime On
                  </div>
                </div>
                <div className="p-3 border-1 rounded-14 mt-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center">
                      <img src={Girl} alt="User avatar" className="rounded-circle me-2" width="40" height="40" />
                      <div className="d-flex flex-column">
                        <div className="fw-bold">Carlos Ward</div>
                        <div className="text-warning small">★ ★ ★ ☆ ☆</div>
                      </div>
                    </div>
                    <div className="text-muted small fw-semibold">20 May</div>
                  </div>
                  <div className="mt-2 text-muted" style={{ fontSize: "14px", lineHeight: "1.5" }}>
                    Lorem Ipsum, Or Lipsum As It Is Sometimes Know Lorem Ipsum, Or Lipsum As It Is Sometime This Lorem Ipsum, Or Lipsum As It Is Sometime On Lorem Ipsum, Or Lipsum
                    As It Is Sometimes Know Lorem Ipsum, Or Lipsum As It Is Sometime This Lorem Ipsum, Or Lipsum As It Is Sometime On
                  </div>
                </div>
                <div className="text-end mt-3">
                  <Link to="/menu-and-restaurant/view-restaurant/reviews" className="text-black">
                    <u>View All</u>
                  </Link>
                </div> */}
              {/* </div> */}
              <div className="review_wrapper">
                {reviews.map((item) => (
                  <div className="p-3 border-1 rounded-14 mt-4" key={item._id}>
                    <div className="d-flex justify-content-between align-items-start">
                      
                      <div className="d-flex align-items-center">
                        <img
                          src={item?.customer?.image || Girl}
                          alt="User avatar"
                          className="rounded-circle me-2"
                          width="40"
                          height="40"
                        />

                        <div className="d-flex flex-column">
                          <div className="fw-bold">
                            {item?.customer?.full_name}
                          </div>

                          <div className="text-warning small">
                            {renderStars(item?.customer_rating)}
                          </div>
                        </div>
                      </div>

                      <div className="text-muted small fw-semibold">
                        {formatDate(item?.createdAt)}
                      </div>
                    </div>

                    <div
                      className="mt-2 text-muted"
                      style={{ fontSize: "14px", lineHeight: "1.5" }}
                    >
                      {item?.customer_review}
                    </div>
                  </div>
                ))}

                <div className="text-end mt-3">
                  <Link
                    to={`/menu-and-restaurant/view-restaurant/reviews/${id}`}
                    className="text-black"
                  >
                    <u>View All</u>
                  </Link>
                </div>
              </div>
              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0">ORDER LIST</h4>
                  <div className="d-flex align-items-center ml-auto">
                    <button
                      type="button"
                      className="btn btn-default rounded-lg mr-2 d-flex align-items-center justify-content-center"
                      onClick={() => fetchOrders()}
                      title="Reload"
                      aria-label="Reload"
                    >
                      <FaSyncAlt />
                    </button>
                    <div className="dropdown text-end w-auto mr-1">
                      <FilterTrigger
                        variant="icon"
                        bordered={false}
                        active={Boolean(ordersDateFilter.from && ordersDateFilter.to)}
                        onClick={() => toggleDropdown(1)}
                      />
                      <div
                        className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${activeDropdown === 1 ? "show" : ""}`}
                        style={{ top: "-150px", right: "40px" }}
                      >
                        {renderDateFilterMenu(1, {
                          appliedFrom: ordersDateFilter.from,
                          appliedTo: ordersDateFilter.to,
                          onApply: (start, end) => {
                            setOrdersDateFilter({ from: start, to: end });
                            setOrdersPage(1);
                          },
                          onReset: () => {
                            setOrdersDateFilter(EMPTY_DATE_FILTER);
                            setOrdersPage(1);
                          },
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="150">Order ID</th>
                          <th width="150">Order Amount</th>
                          <th>Order Date</th>
                          <th>Driver Assigned</th>
                          <th>Order Last Status</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders?.length > 0 ? (
                          orders.map((order) => (
                            <tr key={order._id}>
                              <td className="text-left">#{order.order_number}</td>

                              <td className="text-center">£{order.amount_to_pay}</td>

                              <td>
                                {new Date(order.createdAt).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                })}
                              </td>

                              <td>
                                {order.assigned_driver_id?._id ? (
                                  <Link
                                    to={`/driver-managment/view-driver/${order.assigned_driver_id._id}`}
                                    state={{ from: `/menu-and-restaurant/view-restaurant/${id}` }}
                                    className="text-default"
                                  >
                                    {order.assigned_driver_id.full_name || "N/A"}
                                  </Link>
                                ) : (
                                  "N/A"
                                )}
                              </td>

                              <td>{getOrderStatusLabel(order.order_status)}</td>

                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  <Link
                                    to={`/view-order/${order._id}`}
                                    state={{ from: `/menu-and-restaurant/view-restaurant/${id}` }}
                                    className="mx-2"
                                  >
                                    <img src={viewImage} alt="View" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              No Data Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {/* <tbody> */}
                        {/* <tr>
                          <td className="text-left">#12332423</td>
                          <td className="text-center">£36.00</td>
                          <td>24-April-2025</td>
                          <td>Lorem Ipsum</td>
                          <td>Delivered</td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center">
                              <Link to="/view-order" className="mx-2">
                                <img src={viewImage} alt="View" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-left">#12332423</td>
                          <td className="text-center">£36.00</td>
                          <td>24-April-2025</td>
                          <td>Lorem Ipsum</td>
                          <td>Delivered</td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center">
                              <Link to="/view-order" className="mx-2">
                                <img src={viewImage} alt="View" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-left">#12332423</td>
                          <td className="text-center">£36.00</td>
                          <td>24-April-2025</td>
                          <td>Lorem Ipsum</td>
                          <td>Delivered</td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center">
                              <Link to="/view-order" className="mx-2">
                                <img src={viewImage} alt="View" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-left">#12332423</td>
                          <td className="text-center">£36.00</td>
                          <td>24-April-2025</td>
                          <td>Lorem Ipsum</td>
                          <td>Delivered</td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center">
                              <Link to="/view-order" className="mx-2">
                                <img src={viewImage} alt="View" />
                              </Link>
                            </div>
                          </td>
                        </tr> */}
                        {/* <tr>
                            <td colSpan="6" className="text-center py-4">
                              No Data Found
                            </td>
                          </tr> */}
                      {/* </tbody> */}
                    </table>
                  </div>
                </div>
                <TablePagination page={ordersPage} totalPages={ordersTotalPages} onPageChange={setOrdersPage} />

              </div>
              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0">CALL LIST</h4>
                  <div className="dropdown text-end ml-auto w-auto mr-1">
                    <FilterTrigger
                      variant="icon"
                      bordered={false}
                      active={Boolean(callsDateFilter.from && callsDateFilter.to)}
                      onClick={() => toggleDropdown(3)}
                    />
                    <div
                      className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${activeDropdown === 3 ? "show" : ""}`}
                      style={{ top: "-150px", right: "40px" }}
                    >
                      {renderDateFilterMenu(3, {
                        appliedFrom: callsDateFilter.from,
                        appliedTo: callsDateFilter.to,
                        onApply: (start, end) => {
                          setCallsDateFilter({ from: start, to: end });
                          setCallsPage(1);
                        },
                        onReset: () => {
                          setCallsDateFilter(EMPTY_DATE_FILTER);
                          setCallsPage(1);
                        },
                      })}
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="150">ID</th>
                          <th width="150">Call Date</th>
                          <th>Customer Name</th>
                          <th>Customer Mobile</th>
                          <th>Call Duration</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {callsLoading ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              Loading...
                            </td>
                          </tr>
                        ) : calls.length > 0 ? (
                          calls.map((item) => {
                            const callId = getCallIdLabel(item);
                            const idLabel =
                              callId !== "N/A" && !String(callId).startsWith("#")
                                ? `#${callId}`
                                : callId;
                            return (
                              <tr key={item._id || callId}>
                                <td className="text-left">{idLabel}</td>
                                <td className="text-center">{formatCallDate(getCallDateValue(item))}</td>
                                <td className="text-center">{getCallCustomerName(item)}</td>
                                <td>{getCallCustomerMobile(item)}</td>
                                <td>{formatCallDuration(getCallDurationValue(item))}</td>
                                <td>
                                  <div className="d-flex align-items-center justify-content-center">
                                    <Link
                                      to="/menu-and-restaurant/view-restaurant/call-detail"
                                      state={{
                                        call: item,
                                        from: `/menu-and-restaurant/view-restaurant/${id}`,
                                      }}
                                      className="mx-2"
                                    >
                                      <img src={viewImage} alt="View" />
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              No Data Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TablePagination
                  page={callsPage}
                  totalPages={callsTotalPages}
                  onPageChange={setCallsPage}
                />
              </div>
              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0">SUBSCRIPTION DETAILS</h4>
                  <div className="dropdown  text-end ml-auto w-auto mr-1">
                    <FilterTrigger
                      variant="icon"
                      bordered={false}
                      active={Boolean(subscriptionDateFilter.from && subscriptionDateFilter.to)}
                      onClick={() => toggleDropdown(4)}
                    />
                    <div
                      className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${activeDropdown === 4 ? "show" : ""}`}
                      style={{ top: "-150px", right: "40px" }}
                    >
                      {renderDateFilterMenu(4, {
                        appliedFrom: subscriptionDateFilter.from,
                        appliedTo: subscriptionDateFilter.to,
                        onApply: (start, end) => {
                          setSubscriptionDateFilter({ from: start, to: end });
                          setSubscriptionPage(1);
                          setActiveDropdown(null);
                        },
                        onReset: () => {
                          setSubscriptionDateFilter(EMPTY_DATE_FILTER);
                          setSubscriptionPage(1);
                          setActiveDropdown(null);
                        },
                      })}
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="150">Subscription ID</th>
                          <th width="150">Subscription</th>
                          <th>Payment</th>
                          <th>Subscription Plan</th>
                          <th>Start Date</th>
                          <th>Renewal Date</th>
                          <th>Status</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.length > 0 ? (
                          subscriptions.map((item) => (
                            <tr key={item._id}>
                              <td className="text-left">#{item._id?.slice(-8) || "N/A"}</td>
                              <td className="text-center">
                                {item.plan_name || item.subscription_plan_id?.name || "N/A"}
                              </td>
                              <td>{formatSubscriptionAmount(item)}</td>
                              <td>{formatBillingCycle(item)}</td>
                              <td>{formatSubscriptionDate(item.start_date)}</td>
                              <td>{formatSubscriptionDate(item.expiry_date)}</td>
                              <td>
                                <span
                                  className={`badge ${getSubscriptionStatusClass(
                                    item.subscription_status || item.status
                                  )}`}
                                >
                                  {getSubscriptionStatusLabel(item.subscription_status || item.status)}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  <Link
                                    to={`/menu-and-restaurant/view-restaurant/subscription-details/${item._id}?restaurantViewId=${id}`}
                                    state={{
                                      from: `/menu-and-restaurant/view-restaurant/${id}`,
                                    }}
                                    className="mx-2"
                                  >
                                    <img src={viewImage} alt="View" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TablePagination
                  page={subscriptionPage}
                  totalPages={subscriptionTotalPages}
                  onPageChange={setSubscriptionPage}
                />

              </div>
              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0">DRIVER LIST</h4>
                  <div className="dropdown  text-end ml-auto w-auto mr-1">
                    <FilterTrigger
                      variant="icon"
                      bordered={false}
                      active={Boolean(driversDateFilter.from && driversDateFilter.to)}
                      onClick={() => toggleDropdown(5)}
                    />
                    <div
                      className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${activeDropdown === 5 ? "show" : ""}`}
                      style={{ top: "-150px", right: "40px" }}
                    >
                      {renderDateFilterMenu(5, {
                        appliedFrom: driversDateFilter.from,
                        appliedTo: driversDateFilter.to,
                        onApply: (start, end) => {
                          setDriversDateFilter({ from: start, to: end });
                          setDriversPage(1);
                          setActiveDropdown(null);
                        },
                        onReset: () => {
                          setDriversDateFilter(EMPTY_DATE_FILTER);
                          setDriversPage(1);
                          setActiveDropdown(null);
                        },
                      })}
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="150">Order ID</th>
                          <th width="150">Name</th>
                          <th>Mobile Number</th>
                          <th>Email id</th>
                          <th>Status</th>
                          <th>Availability</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDrivers.length > 0 ? (
                          paginatedDrivers.map((item) => (
                            <tr key={item._id}>
                              <td>#{item._id.slice(-8)}</td>
                              <td>{item?.driver?.full_name || "No data found"}</td>
                              <td>{item?.driver?.phone || "No data found"}</td>
                              <td>{item?.driver?.email || "No data found"}</td>

                              <td>
                                <span className={`badge ${item?.driver?.status === 1 ? "bg-success" : "bg-danger"}`}>
                                  {item?.driver?.status === 1 ? "Active" : "Inactive"}
                                </span>
                              </td>

                              <td>
                                <span className={`badge ${item?.is_available ? "bg-success" : "bg-secondary"}`}>
                                  {item?.is_available ? "Available" : "Offline"}
                                </span>
                              </td>

                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  <Link
                                    to={`/driver-managment/view-driver/${item?.driver?._id}`}
                                    state={{ from: `/menu-and-restaurant/view-restaurant/${id}` }}
                                    className="mx-2"
                                  >
                                    <img src={viewImage} alt="View" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              No Data Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TablePagination page={driversPage} totalPages={driversTotalPages} onPageChange={setDriversPage} />

              </div>
              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0">RESTAURANT DELIVERY PINCODES</h4>
                  <div className="dropdown  text-end ml-auto w-auto mr-1">
                    <FilterTrigger
                      variant="icon"
                      bordered={false}
                      active={Boolean(deliveryDateFilter.from && deliveryDateFilter.to)}
                      onClick={() => toggleDropdown(6)}
                    />
                    <div
                      className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${activeDropdown === 6 ? "show" : ""}`}
                      style={{ top: "-150px", right: "40px" }}
                    >
                      {renderDateFilterMenu(6, {
                        appliedFrom: deliveryDateFilter.from,
                        appliedTo: deliveryDateFilter.to,
                        onApply: (start, end) => {
                          setDeliveryDateFilter({ from: start, to: end });
                          setDeliveryPage(1);
                        },
                        onReset: () => {
                          setDeliveryDateFilter(EMPTY_DATE_FILTER);
                          setDeliveryPage(1);
                        },
                      })}
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="80">S No</th>
                          <th>City</th>
                          <th>Pincode</th>
                          <th>Location</th>
                          <th>Delivery Charge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveryCities.length > 0 ? (
                          deliveryCities.map((item, index) => (
                            <tr key={item._id || index}>
                              <td className="text-left">
                                {(deliveryPage - 1) * TABLE_PAGE_LIMIT + index + 1}
                              </td>
                              <td>{item.city_name || "N/A"}</td>
                              <td>{item.pincode || "N/A"}</td>
                              <td>{item.location || "N/A"}</td>
                              <td>
                                {item.delivery_charge != null && item.delivery_charge !== ""
                                  ? `£${item.delivery_charge}`
                                  : "N/A"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4">
                              No Data Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TablePagination page={deliveryPage} totalPages={deliveryTotalPages} onPageChange={setDeliveryPage} />

              </div>
              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0">COMMISSION SCHEDULE</h4>
                  <div className="d-flex align-items-center ml-auto">
                    <button
                      type="button"
                      className="btn btn-primary rounded-lg px-3 mr-2"
                      disabled={hasPendingSchedule || schedulesLoading}
                      title={
                        hasPendingSchedule
                          ? "An active schedule already exists. You can add a new one after it expires."
                          : "Add commission schedule"
                      }
                      onClick={openAddScheduleModal}
                    >
                      Add
                    </button>
                    <div className="dropdown text-end w-auto mr-1">
                      <FilterTrigger
                        variant="icon"
                        bordered={false}
                        active={Boolean(scheduleDateFilter.from && scheduleDateFilter.to)}
                        onClick={() => toggleDropdown(8)}
                      />
                      <div
                        className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${activeDropdown === 8 ? "show" : ""}`}
                        style={{ top: "-150px", right: "40px" }}
                      >
                        {renderDateFilterMenu(8, {
                          appliedFrom: scheduleDateFilter.from,
                          appliedTo: scheduleDateFilter.to,
                          onApply: (start, end) => {
                            setScheduleDateFilter({ from: start, to: end });
                            setSchedulePage(1);
                            setActiveDropdown(null);
                          },
                          onReset: () => {
                            setScheduleDateFilter(EMPTY_DATE_FILTER);
                            setSchedulePage(1);
                            setActiveDropdown(null);
                          },
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="150">S No</th>
                          <th width="150">Start Date</th>
                          <th>End Date</th>
                          <th>Commission</th>
                          <th>Applied Date</th>
                          <th>Status</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedulesLoading ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        ) : schedules.length > 0 ? (
                          schedules.map((item, index) => (
                            <tr key={item._id || `schedule-${index}`}>
                              <td className="text-left">{(schedulePage - 1) * TABLE_PAGE_LIMIT + index + 1}</td>
                              <td className="text-center">
                                {formatSubscriptionDate(item.start_date || item.startDate)}
                              </td>
                              <td>{formatSubscriptionDate(item.end_date || item.endDate)}</td>
                              <td>
                                {getCommissionTypeLabel(
                                  item.commission ||
                                    item.commission_type ||
                                    item.commision_type ||
                                    item.commission_name
                                )}
                              </td>
                              <td>
                                {formatSubscriptionDate(
                                  item.applied_date || item.appliedDate || item.createdAt
                                )}
                              </td>
                              <td>
                                <span className={`badge ${getCommissionScheduleStatusClass(item.status)}`}>
                                  {getCommissionScheduleStatusLabel(item.status)}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  {Number(item.status) === 1 ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 mx-2"
                                        title="Edit"
                                        disabled={scheduleSubmitting || deletingScheduleId === item._id}
                                        onClick={() => openEditScheduleModal(item)}
                                      >
                                        <img src={editImage} alt="Edit" />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 mx-2"
                                        title="Delete"
                                        disabled={scheduleSubmitting || deletingScheduleId === item._id}
                                        onClick={() => handleDeleteCommissionSchedule(item._id)}
                                      >
                                        <img src={DeleteIcon} alt="Delete" />
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TablePagination
                  page={schedulePage}
                  totalPages={scheduleTotalPages}
                  onPageChange={setSchedulePage}
                />
              </div>

              <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
                <div className="card-heading d-flex align-items-center pb-3">
                  <h4 className="my-0">RESTAURANT COMMISSION LOG</h4>
                  <div className="dropdown  text-end ml-auto w-auto mr-1">
                    <FilterTrigger
                      variant="icon"
                      bordered={false}
                      active={Boolean(commissionDateFilter.from && commissionDateFilter.to)}
                      onClick={() => toggleDropdown(7)}
                    />
                    <div
                      className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${activeDropdown === 7 ? "show" : ""}`}
                      style={{ top: "-150px", right: "40px" }}
                    >
                      {renderDateFilterMenu(7, {
                        appliedFrom: commissionDateFilter.from,
                        appliedTo: commissionDateFilter.to,
                        onApply: (start, end) => {
                          setCommissionDateFilter({ from: start, to: end });
                          setCommissionPage(1);
                          setActiveDropdown(null);
                        },
                        onReset: () => {
                          setCommissionDateFilter(EMPTY_DATE_FILTER);
                          setCommissionPage(1);
                          setActiveDropdown(null);
                        },
                      })}
                    </div>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <div className="table-responsive px-0">
                    <table className="table table-striped trim">
                      <thead>
                        <tr>
                          <th width="150">S No</th>
                          <th width="150">Start Date</th>
                          <th>End Date</th>
                          <th>Commission</th>
                          <th>Applied Date</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissionsLoading ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        ) : commissions.length > 0 ? (
                          commissions.map((item, index) => (
                            <tr key={item._id || `commission-${index}`}>
                              <td className="text-left">{(commissionPage - 1) * TABLE_PAGE_LIMIT + index + 1}</td>
                              <td className="text-center">
                                {formatSubscriptionDate(item.start_date || item.startDate)}
                              </td>
                              <td>{formatSubscriptionDate(item.end_date || item.endDate)}</td>
                              <td>
                                {getCommissionTypeLabel(getCommissionTypeValue(item))}
                              </td>
                              <td>
                                {formatSubscriptionDate(
                                  item.applied_date || item.appliedDate || item.createdAt
                                )}
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  {isDiscountedCommissionLog(item) ? (
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 mx-2"
                                      title="Edit end date"
                                      disabled={commissionEndDateSubmitting}
                                      onClick={() => openEditCommissionEndDateModal(item)}
                                    >
                                      <img src={editImage} alt="Edit" />
                                    </button>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TablePagination
                  page={commissionPage}
                  totalPages={commissionTotalPages}
                  onPageChange={setCommissionPage}
                />

              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="bg-black">
          <Modal.Title>Approve Restaurant</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black">
          <form onSubmit={handleApprove}>
            <div className="form-group mb-3">
              <label>Commission Type</label>
              <select
                className="form-control"
                name="commissionType"
                value={approveForm.commissionType}
                onChange={handleApproveFormChange}
                required
                style={{ colorScheme: "dark" }}
              >
                <option value="">Select Type</option>
                <option value={COMMISSION_TYPES.STANDARD}>Standard Commission</option>
                <option value={COMMISSION_TYPES.DISCOUNTED}>Discounted Commission</option>
              </select>
            </div>
            {!isStandardCommissionType(approveForm.commissionType) &&
              approveForm.commissionType === COMMISSION_TYPES.DISCOUNTED && (
              <>
                <div className="form-group mb-3">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="startDate"
                    value={approveForm.startDate}
                    onChange={handleApproveFormChange}
                    required={approveForm.commissionType === COMMISSION_TYPES.DISCOUNTED}
                    style={{ colorScheme: "dark" }}
                  />
                </div>
                <div className="form-group mb-3">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="endDate"
                    value={approveForm.endDate}
                    onChange={handleApproveFormChange}
                    required={approveForm.commissionType === COMMISSION_TYPES.DISCOUNTED}
                    style={{ colorScheme: "dark" }}
                  />
                </div>
              </>
            )}
            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleClose} className="mr-2" disabled={actionLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="success" disabled={actionLoading}>
                {actionLoading ? "Processing..." : "Approve"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <Modal show={RejectShow} onHide={handleClose} centered>
        <Modal.Header closeButton className="bg-black">
          <Modal.Title>Reject Restaurant</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black">
          <div className="form-group mb-3">
            <label>Reject Reason</label>
            <textarea
              className="form-control"
              rows="3"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection"
              required
            />
          </div>
          <div className="d-flex justify-content-end">
            <Button variant="secondary" onClick={handleClose} className="mr-2" disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
              {actionLoading ? "Processing..." : "Reject"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal show={showScheduleModal} onHide={handleScheduleModalClose} centered>
        <Modal.Header closeButton className="bg-black">
          <Modal.Title>
            {editingScheduleId ? "Edit Commission Schedule" : "Add Commission Schedule"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black">
          <form onSubmit={handleSaveCommissionSchedule}>
            <div className="form-group mb-3">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                name="start_date"
                value={scheduleForm.start_date}
                onChange={handleScheduleFormChange}
                required
                disabled={scheduleSubmitting}
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="form-group mb-3">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                name="end_date"
                value={scheduleForm.end_date}
                onChange={handleScheduleFormChange}
                min={scheduleForm.start_date || undefined}
                required
                disabled={scheduleSubmitting}
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={handleScheduleModalClose}
                className="mr-2"
                disabled={scheduleSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={scheduleSubmitting}>
                {scheduleSubmitting
                  ? "Saving..."
                  : editingScheduleId
                    ? "Update"
                    : "Add"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <Modal show={showCommissionEndDateModal} onHide={handleCommissionEndDateModalClose} centered>
        <Modal.Header closeButton className="bg-black">
          <Modal.Title>Edit Commission End Date</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black">
          <form onSubmit={handleUpdateCommissionEndDate}>
            <div className="form-group mb-3">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={commissionEndDateForm.end_date}
                onChange={(e) =>
                  setCommissionEndDateForm((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                min={commissionEndDateForm.start_date || undefined}
                required
                disabled={commissionEndDateSubmitting}
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={handleCommissionEndDateModalClose}
                className="mr-2"
                disabled={commissionEndDateSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={commissionEndDateSubmitting}>
                {commissionEndDateSubmitting ? "Saving..." : "Update"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ViewRestaurant;
